import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageCircle, Moon } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/kit";

const Meter = ({ icon: Icon, label, t, extra, testId }) => (
  <div className="mm-soft p-4 flex-1 min-w-[12rem]" data-testid={testId}>
    <div className="flex items-center gap-2 font-semibold text-sm"><Icon size={16} className="text-mm-primary" />{label}</div>
    <div className="text-2xl font-bold mt-1">{t.used} <span className="text-sm font-normal text-mm-muted">of {t.limit} today</span></div>
    {extra > 0 && <div className="text-sm text-mm-primary font-semibold">+{extra} extra from your pack</div>}
  </div>
);

export default function MoreAI() {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);
  const load = () => api.get("/ai-packs").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    const pid = params.get("paymentId"), payer = params.get("PayerID");
    if (pid && payer && !ran.current) {
      ran.current = true;
      api.post("/ai-packs/paypal/execute", { payment_id: pid, payer_id: payer })
        .then(() => toast.success("Extra allowance added 💜"))
        .catch((e) => toast.error(errMsg(e)))
        .finally(() => { setParams({}, { replace: true }); load(); });
    } else {
      if (params.get("cancelled")) { ran.current = true; toast("Payment cancelled. You haven't been charged."); setParams({}, { replace: true }); }
      load();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const buy = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai-packs/paypal/checkout", { origin_url: window.location.origin }); window.location.assign(data.checkout_url); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!d) return <Spinner />;
  const extra = (k) => d.packs.reduce((n, p) => n + (p.left[k] || 0), 0);
  return (
    <div className="max-w-3xl" data-testid="more-ai-page">
      <PageHeader back title="More AI time" subtitle="Daily limits keep Mindful Me affordable for everyone. Need a bit more? Add an extra allowance." />
      <div className="flex flex-wrap gap-3 mb-6">
        <Meter icon={Moon} label="New sleep stories" t={d.today.story} extra={extra("story")} testId="more-ai-meter-story" />
        <Meter icon={MessageCircle} label="Mia messages" t={d.today.chat} extra={extra("chat")} testId="more-ai-meter-chat" />
      </div>
      <div className="mm-card p-6 border-2 border-mm-primary/30" data-testid="more-ai-offer">
        <div className="text-xs font-bold uppercase tracking-wider text-mm-primary">Extra allowance</div>
        <div className="text-3xl font-bold mt-1" data-testid="more-ai-price">${d.offer.price.toFixed(2)} <span className="text-base font-normal text-mm-muted">one-time</span></div>
        <ul className="mt-3 space-y-1 text-sm">
          <li>🌙 +{d.offer.story} new sleep stories</li>
          <li>💬 +{d.offer.chat} Mia messages</li>
          <li>📅 Use them any day for {d.offer.days} days, after your daily allowance runs out</li>
        </ul>
        <button onClick={buy} disabled={busy} className="mt-5 w-full sm:w-auto rounded-full bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] font-bold px-8 py-3.5 inline-flex items-center justify-center gap-2 transition-colors" data-testid="more-ai-paypal">
          {busy && <Loader2 size={16} className="animate-spin" />}Pay with PayPal
        </button>
        <p className="text-xs text-mm-muted mt-3">SOS Calm, your safety plan, crisis lines and all non-AI tools never have limits.</p>
      </div>
      {d.packs.length > 0 && (
        <div className="mm-card p-5 mt-6" data-testid="more-ai-packs">
          <h2 className="font-bold mb-2">Your active packs</h2>
          {d.packs.map((p) => <div key={p.id} className="text-sm py-1.5 border-b border-mm-line last:border-0" data-testid={`more-ai-pack-${p.id}`}>{p.left.story} stories · {p.left.chat} messages left · until {new Date(p.expires_at).toLocaleDateString()}</div>)}
        </div>
      )}
    </div>
  );
}
